import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { hiringService } from "../../services/api";
import { DsButton, DsInput, DsLabel, DsSpinner } from "../../design-system";

/**
 * Token-gated candidate profile page — profile fill only (no admin Hiring access).
 */
export default function CandidateProfileInvitePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await hiringService.getPublicProfile(token);
        if (cancelled) return;
        const data = res?.data || res;
        setMeta(data);
        setForm({ ...(data.profile || {}) });
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              "This invite link is invalid or expired."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const res = await hiringService.savePublicProfile(token, form);
      setMessage(res?.message || "Profile saved");
      if (res?.data?.profileCompletionPercent != null) {
        setMeta((m) => ({
          ...m,
          profileCompletionPercent: res.data.profileCompletionPercent,
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--ds-bg))]">
        <DsSpinner />
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--ds-bg))] p-4">
        <div className="max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6 text-center">
          <h1 className="ds-h3 mb-2">Profile invite</h1>
          <p className="ds-body text-[hsl(var(--ds-danger))]">{error}</p>
        </div>
      </div>
    );
  }

  const fields = [
    { key: "name", label: "Full name" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Mobile" },
    { key: "dob", label: "Date of birth", type: "date" },
    { key: "gender", label: "Gender" },
    { key: "maritalStatus", label: "Marital status" },
    { key: "address", label: "Address" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "zip", label: "PIN / ZIP" },
    { key: "country", label: "Country" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "noticePeriod", label: "Notice period" },
    { key: "currentSalary", label: "Current CTC" },
    { key: "expectedSalary", label: "Expected CTC" },
  ];

  const mandatory = new Set(meta?.mandatoryFields || []);

  return (
    <div className="min-h-screen bg-[hsl(var(--ds-bg))] py-8">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]">
          <h1 className="ds-h2">Complete your profile</h1>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Candidate {meta?.candidateCode} · Profile completion{" "}
            {meta?.profileCompletionPercent ?? 0}%
          </p>
          <p className="ds-caption mt-2 text-[hsl(var(--ds-muted-fg))]">
            This page is only for your profile details. You cannot access company
            hiring admin screens from here.
          </p>
        </div>

        <form
          onSubmit={onSave}
          className="space-y-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.key === "address" ? "md:col-span-2" : ""}>
                <DsLabel>
                  {f.label}
                  {mandatory.has(f.key) ? " *" : ""}
                </DsLabel>
                <DsInput
                  type={f.type || "text"}
                  required={mandatory.has(f.key)}
                  value={form[f.key] || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, [f.key]: e.target.value }))
                  }
                />
              </div>
            ))}
            <label className="flex items-center gap-2 ds-body md:col-span-2">
              <input
                type="checkbox"
                checked={!!form.isFresher}
                onChange={(e) =>
                  setForm((p) => ({ ...p, isFresher: e.target.checked }))
                }
              />
              I am a fresher
            </label>
          </div>

          {error && (
            <p className="ds-caption text-[hsl(var(--ds-danger))]">{error}</p>
          )}
          {message && (
            <p className="ds-caption text-[hsl(var(--ds-success))]">{message}</p>
          )}

          <div className="flex justify-end">
            <DsButton type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save profile"}
            </DsButton>
          </div>
        </form>
      </div>
    </div>
  );
}
